import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Send,
  Settings,
  Bell,
  Save,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailSettings() {
  const [settings, setSettings] = useState({
    sender_email: "onboarding@resend.dev",
    recipient_email: "",
    defect_threshold: 10,
    notifications_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/email`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, settings);
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!settings.recipient_email) {
      toast.error("Bitte Empfänger-E-Mail eingeben");
      return;
    }

    setTesting(true);
    try {
      await axios.post(`${API}/settings/email/test`);
      toast.success("Test-E-Mail gesendet");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Senden";
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="email-settings">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </Link>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-900">
                E-Mail-Einstellungen
              </h1>
              <p className="text-sm text-slate-500">Benachrichtigungen konfigurieren</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Email Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              E-Mail-Konfiguration
            </CardTitle>
            <CardDescription>
              Absender- und Empfängeradresse für Benachrichtigungen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sender">Absender-E-Mail</Label>
              <Input
                id="sender"
                type="email"
                value={settings.sender_email}
                onChange={(e) => setSettings({...settings, sender_email: e.target.value})}
                placeholder="noreply@example.com"
              />
              <p className="text-xs text-slate-500">
                Hinweis: Bei Resend im Test-Modus muss "onboarding@resend.dev" verwendet werden
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient">Empfänger-E-Mail</Label>
              <Input
                id="recipient"
                type="email"
                value={settings.recipient_email}
                onChange={(e) => setSettings({...settings, recipient_email: e.target.value})}
                placeholder="admin@example.com"
              />
              <p className="text-xs text-slate-500">
                E-Mail-Adresse für Benachrichtigungen (z.B. Admin oder Lagerleitung)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Notification Triggers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Benachrichtigungs-Trigger
            </CardTitle>
            <CardDescription>
              Automatische E-Mail-Benachrichtigungen konfigurieren
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Benachrichtigungen aktivieren</Label>
                <p className="text-sm text-slate-500">
                  E-Mails bei Erreichen des Defekt-Schwellwerts senden
                </p>
              </div>
              <Switch
                checked={settings.notifications_enabled}
                onCheckedChange={(checked) => setSettings({...settings, notifications_enabled: checked})}
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label htmlFor="threshold" className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Defekt-Schwellwert
              </Label>
              <Input
                id="threshold"
                type="number"
                min="1"
                max="100"
                value={settings.defect_threshold}
                onChange={(e) => setSettings({...settings, defect_threshold: parseInt(e.target.value) || 10})}
                className="w-32"
              />
              <p className="text-xs text-slate-500">
                E-Mail wird gesendet, wenn diese Anzahl defekter Geräte erreicht wird
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleTestEmail}
            disabled={testing || !settings.recipient_email}
          >
            {testing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900 mr-2"></div>
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Test-E-Mail senden
          </Button>
          
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Hinweis:</strong> Um E-Mails zu senden, muss ein gültiger Resend API-Key 
            in der Backend-Konfiguration hinterlegt sein. Die E-Mail-Benachrichtigung wird 
            automatisch ausgelöst, wenn mehr als {settings.defect_threshold} Geräte als 
            defekt markiert werden (maximal eine E-Mail pro 24 Stunden).
          </p>
        </div>
      </main>
    </div>
  );
}
